import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import { useParams } from "react-router-dom";

import { useState } from "react";
import { useGetGroupByIdQuery } from "@/api/groupApi/group";
import {
  useAssignAgentToGroupMutation,
  useGetAgentsInGroupQuery,
  useEditAgentInGroupMutation,
} from "@/api/groupApi/agentGroup";
import { useAddCategoriesToGroupMutation } from "@/api/groupApi/groupCategory";
import LoadingPage from "@/components/LoadingPage";
import { Button, Badge, Breadcrumb } from "flowbite-react";
import GroupAgentTable from "@/components/groups/GroupAgentTable";
import GroupCategoriesTable from "@/components/groups/GroupCategoriesTable";
import AgentListDialog from "@/components/groups/AgentListDialog";
import CategoryListDialog from "@/components/groups/CategoryListDialog";
import { toast } from "react-toastify";
import { DataGridPagination } from "@/sharedTypes";
import { HiHome, HiUserGroup, HiPlus } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "@/utils/requestUtils";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { agentRoles } from "@/constants/agent";

const GroupDetails = () => {
  const { t } = useTranslation(["group", "common"]);
  const user = useCurrentUser();
  const { id } = useParams<{ id: string }>();
  const [isAgentListDialogOpen, setIsAgentListDialogOpen] = useState(false);
  const [isCategoryListDialogOpen, setIsCategoryListDialogOpen] = useState(false);
  const [agentPage, setAgentPage] = useState(1);
  const agentPageSize = 10;

  const { data: groupDetails, isLoading: isGroupLoading } =
    useGetGroupByIdQuery(id as string, {
      skip: !id,
    });

  const { data: agentsInGroup, isLoading: isAgentsLoading } =
    useGetAgentsInGroupQuery(
      {
        groupId: id as string,
        params: {
          pagination: { page: agentPage, pageSize: agentPageSize },
          filters: {},
        },
      },
      { skip: !id },
    );

  const [assignAgent] = useAssignAgentToGroupMutation();
  const [editAgent] = useEditAgentInGroupMutation();
  const [addCategories] = useAddCategoriesToGroupMutation();

  const handleAssignAgents = async (agentIds: string[]) => {
    const groupId = parseInt(id as string);
    try {
      for (const agentId of agentIds) {
        await assignAgent({
          agent_id: parseInt(agentId),
          group_ids: [groupId],
          is_admin: false,
        }).unwrap();
      }

      toast.success(t("group:assignAgentSuccess"));
      setIsAgentListDialogOpen(false);
    } catch (error) {
      toast.error(t("group:assignAgentFail"));
    }
  };

  const handleAddCategories = async (categoryIds: number[]) => {
    try {
      await addCategories({ groupId: id as string, category_ids: categoryIds }).unwrap();
      toast.success(t("group:categoryAddedSuccess"));
      setIsCategoryListDialogOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleMakeAdmin = async (agentId: string, isAdmin: boolean) => {
    try {
        await editAgent({
            agent_id: parseInt(agentId),
            group_id: parseInt(id as string),
            is_admin: isAdmin
        }).unwrap();
        toast.success(t("group:updateAgentSuccess")); // Need to ensure translation key exists or use generic
    } catch (error) {
        toast.error(getErrorMessage(error));
    }
  };

  const paginationData = agentsInGroup?.pagination || agentsInGroup || {};

  const agentPagination: DataGridPagination = {
    pageIndex: paginationData.pageIndex ?? paginationData.page ?? 1,
    pageSize: paginationData.pageSize ?? 10,
    totalPages: paginationData.totalPages ?? 1,
    totalItems: paginationData.totalItems ?? paginationData.total ?? 0,
  };

  return (
    <NavbarSidebarLayout>
      {isGroupLoading ? <LoadingPage /> : !groupDetails ? <div className="p-4">{t("group:groupNotFound")}</div> : <div className="h-screen overflow-y-auto pb-20">
        <div className="mb-6 grid grid-cols-1 gap-y-6 px-4 pt-6 dark:border-gray-700 dark:bg-gray-900">
          <div className="col-span-full space-y-8">
            <Breadcrumb className="mb-4">
              <Breadcrumb.Item href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">{t("common:home")}</span>
                </div>
              </Breadcrumb.Item>
              <Breadcrumb.Item href="/groups">
                <div className="flex items-center gap-x-3">
                  <HiUserGroup className="text-xl" />
                  <span className="dark:text-white">{t("common:groups")}</span>
                </div>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{groupDetails.group_name}</Breadcrumb.Item>
            </Breadcrumb>

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                  {groupDetails.group_name}
                </h1>
                <p className="text-gray-500">
                  {groupDetails.group_description}
                </p>
              </div>

              <Badge size="md" color={groupDetails.is_active ? "success" : "failure"}>
                {groupDetails.is_active ? t("common:active") : t("common:inactive")}
              </Badge>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t("group:agents")}</h2>
                {user?.agent_role === agentRoles.company_admin && (
                  <Button
                    color="primary"
                    onClick={() => setIsAgentListDialogOpen(true)}
                  >
                      <HiPlus className="mr-2 h-5 w-5" />
                    {t("group:addAgent")}
                  </Button>
                )}
              </div>
              <GroupAgentTable
                data={agentsInGroup?.items || []}
                pagination={agentPagination}
                onPageChange={setAgentPage}
                groupId={id}
                onToggleAdmin={handleMakeAdmin}
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t("group:categories")}</h2>
                {user?.agent_role === agentRoles.company_admin && (
                  <Button
                    color="primary"
                    onClick={() => setIsCategoryListDialogOpen(true)}
                  >
                    <HiPlus className="mr-2 h-5 w-5" />
                    {t("group:addCategory")}
                  </Button>
                )}
              </div>
              <GroupCategoriesTable groupId={id as string} />
            </div>
          </div>
        </div>
        <AgentListDialog
          open={isAgentListDialogOpen}
          onClose={() => setIsAgentListDialogOpen(false)}
          onSubmit={handleAssignAgents}
        />
        <CategoryListDialog
          open={isCategoryListDialogOpen}
          onClose={() => setIsCategoryListDialogOpen(false)}
          onSubmit={handleAddCategories}
        />
      </div>}
    </NavbarSidebarLayout>
  );
};

export default GroupDetails;
